import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { TransactionHistory } from "@/components/currency/TransactionHistory";
import { Shop } from "@/components/currency/Shop";
import { TopUp } from "@/components/currency/TopUp";
import { UserPurchases } from "@/components/currency/UserPurchases";
import { useLocation, useSearchParams } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/ui/app-layout";
import { DeckMarketplaceContent } from '@/components/marketplace/deck-marketplace-content';
import { useQueryClient } from "@tanstack/react-query";
import PullToRefresh from "react-simple-pull-to-refresh";
import {
  History,
  Store,
  CreditCard,
  Backpack,
  LayoutGrid,
  Package,
  RefreshCw
} from "lucide-react";
import { EnhancedHeader } from "@/components/ui/enhanced-header";

export default function ShopPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("marketplace");
  const [_, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Check if there's a tab parameter in the URL
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
    }

    // Check if this is a payment callback
    const reference = searchParams.get("reference");
    if (reference) {
      setActiveTab("topup");
    }

    // Check for currency tab and subtab
    const currencyTab = searchParams.get("currency");
    if (currencyTab) {
      setActiveTab("topup");
    }
  }, [searchParams]);

  if (!user) {
    setLocation("/auth");
    return null;
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value);

    // Update URL with tab parameter without navigation
    if (value !== "shop") {
      setLocation(`/shop?tab=${value}`, { replace: true });
    } else {
      setLocation("/shop", { replace: true });
    }
  };

  // Handle pull-to-refresh by invalidating queries based on active tab
  const handleRefresh = async () => {
    switch (activeTab) {
      case "marketplace":
        await queryClient.invalidateQueries({ queryKey: ["deck"] });
        break;
      case "shop":
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/shop"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/balance"] });
        break;
      case "topup":
        await queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
        await queryClient.invalidateQueries({ queryKey: ["coinPackages"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/balance"] });
        break;
      case "purchases":
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/purchases"] });
        break;
      case "transactions":
        await queryClient.invalidateQueries({ queryKey: ["transactions"] });
        
        break;
      default:
        // Invalidate all shop-related queries to be safe
        await queryClient.invalidateQueries({ queryKey: ["deck"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/shop"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/balance"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/currency/purchases"] });
        break;
    }
    return Promise.resolve();
  };

  return (
    <AppLayout>
      <EnhancedHeader
        title="Shop"
        showBackButton={false}
        backPath="/"
        coloredText={true}
        rightContent={
          <div className="flex items-center gap-2">
            <CurrencyDisplay variant="icon" size="sm" />
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
              onClick={() => setLocation("/settings")}
            >
              <Backpack className="h-4 w-4" />
            </Button>
          </div>
        }
        showScrollEffect={true}
      />

      <PullToRefresh
        onRefresh={handleRefresh}
        pullingContent={
          <div className="text-center p-2">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
          </div>
        }
        refreshingContent={
          <div className="text-center p-4">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Refreshing...</p>
          </div>
        }
      >
        <div className="py-2 px-4 sm:px-6 lg:px-8">
          <Tabs
            defaultValue="marketplace"
            onValueChange={handleTabChange}
          >
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="marketplace" className="flex items-center gap-1">
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Decks</span>
              </TabsTrigger>
              <TabsTrigger value="shop" className="flex items-center gap-1">
                <Store className="h-4 w-4" />
                <span className="hidden sm:inline">Shop</span>
              </TabsTrigger>
              <TabsTrigger value="topup" className="flex items-center gap-1">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Top Up</span>
              </TabsTrigger>
              <TabsTrigger value="purchases" className="flex items-center gap-1">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">My Items</span>
              </TabsTrigger>
              <TabsTrigger value="transactions" className="flex items-center gap-1">
                <History className="h-4 w-4" />
                <span className="hidden sm:inline">History</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="marketplace" className="py-2">
              <DeckMarketplaceContent
                showFeaturedDeck={false}
                onSelectDeck={(deckId) => setLocation(`/deck/${deckId}?returnTo=shop`)}
              />
            </TabsContent>

            <TabsContent value="shop" className="py-2">
              <Shop />
            </TabsContent>

            <TabsContent value="topup" className="py-2">
              <TopUp />
            </TabsContent>

            <TabsContent value="purchases" className="py-4">
              <UserPurchases />
            </TabsContent>

            <TabsContent value="transactions" className="py-4">
              <div className="space-y-8">
                <TransactionHistory limit={20} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </PullToRefresh>
    </AppLayout>
  );
}
